import * as React from "react";
import { useMemo, useState } from "react";
import { ActionMeta, InputActionMeta, OnChangeValue, PropsValue } from "react-select";

export interface UseDropdownProps<OptionItem> {
  defaultValue?: PropsValue<OptionItem>;
  onSelect?: (value?: OptionItem | null) => any;
  includeAll?: boolean;
  multiple?: boolean;
  optionData?: (OptionItem & {
    id?: number | string;
    icon?: React.ReactNode;
    value?: number | string | boolean;
  })[];
  getLabel?: (i: OptionItem) => string;
  getValue?: (i: OptionItem) => string;
}

export const OPTION_ALL_UNIQ_VALUE = "this-is-all-value-from-rayyone-with-love";
export const OPTION_ALL = { label: "All", value: OPTION_ALL_UNIQ_VALUE };

export interface BaseOption<Item = {}> {
  name?: string;
  label: string;
  value?: any;
  item?: Item;
}

export function useDropdown<OptionItem extends BaseOption, IsMulti = false>({
  defaultValue,
  multiple,
  onSelect,
  includeAll,
  optionData = [],
  getLabel = (i: OptionItem) => i.name || i.label || "",
  getValue,
}: UseDropdownProps<OptionItem>) {
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [value, setValue] = useState(defaultValue || (includeAll ? OPTION_ALL : undefined));

  const options = useMemo(() => {
    // let defaultOptions = defaultValue ? (isArray(defaultValue) ? [...(defaultValue || [])] : [defaultValue]) : [];
    const res: { label: string; value: string; icon?: React.ReactNode }[] = optionData?.map(item => {
      // if (defaultIds?.includes(item.id)) {
      //   defaultOptions = defaultOptions.filter(o => o.id !== item.id);
      // }
      const optionValue = getValue ? getValue(item) : item?.id?.toString() ?? item?.value ?? "";

      if (optionValue === undefined) {
        throw new Error("Fatal: Value is undefined!");
      }
      return {
        label: getLabel(item),
        value: optionValue,
        icon: item.icon,
        item,
      };
    });
    // defaultOptions.forEach(o => res.unshift({label: getLabel(o), value: o.id.toString()}));
    if (includeAll) {
      res.unshift(OPTION_ALL);
    }
    return res;
  }, [optionData, includeAll, getLabel, getValue]);

  const onInputChange: (newValue: string, actionMeta: InputActionMeta) => void = searchValue => {
    if (searchValue === OPTION_ALL_UNIQ_VALUE) {
      setSearchQuery(undefined);
    } else {
      setSearchQuery(searchValue);
    }
  };

  const onChange: (
    newValue: OnChangeValue<OptionItem, false>,
    actionMeta: ActionMeta<OptionItem>
  ) => void = selectedOption => {
    if (!selectedOption?.value) {
      setSearchQuery(undefined);
      setValue(undefined);
      onSelect?.(selectedOption || ({} as OptionItem));
      return;
    }

    setValue(selectedOption);
    if (selectedOption?.value === OPTION_ALL_UNIQ_VALUE) {
      onSelect?.({ value: undefined } as OptionItem);
      return;
    }
    onSelect?.(selectedOption);
  };

  return {
    value,
    setValue,
    searchQuery,
    options,
    onInputChange,
    onChange,
  };
}
